import type { Acao } from './Hitter';

/**
 * Qual animacao o corpo esta' fazendo, dado o que o `Motor` diz.
 *
 * Funcao pura, e por isso tem teste. A escolha parece obvia lida em voz alta
 * ("parado toca Idle, andando toca Run") e nao e': o atleta deste jogo encara a
 * BOLA enquanto anda relativo a' camera, ou seja, ele anda de lado quase o
 * tempo todo. Com um `Run` frontal so', metade da partida seria o boneco
 * deslizando de lado com as pernas correndo pra frente.
 *
 * O pack traz as quatro direcoes casando entre si, e e' o que esta' logica
 * existe pra usar.
 */

/**
 * Qual gesto cada toque vira.
 *
 * `cortada` e `ataque` sao a mesma batida por cima, e ja' foram o mesmo clipe:
 * a cortada saia de perna reta no ar. Agora a do ar tem pernas proprias.
 */
export const CLIPE_DO_TOQUE: Readonly<Record<Acao, string>> = {
  manchete: 'Manchete',
  levantamento: 'Levantamento',
  cortada: 'Cortada',
  ataque: 'Ataque',
  saque: 'Saque',
};

/** O que o corpo esta' fazendo agora. Tudo sai do `Motor` e do `Hitter`. */
export interface EstadoDoCorpo {
  noChao: boolean;
  mergulhando: boolean;
  levantando: boolean;
  /**
   * O toque que o corpo esta' acompanhando agora, ou null.
   *
   * Quem liga e desliga isto e' o atleta, com um relogio: o `Hitter` resolve o
   * toque num quadro so', e o gesto tem que durar o clipe inteiro depois dele.
   */
  gesto: Acao | null;
  /**
   * Qual toque e' este. E' a contagem do `Hitter`, e serve pra um caso so':
   * dois toques iguais seguidos. Sem ele o segundo nao reiniciaria o clipe,
   * porque o NOME do clipe nao mudou, e a manchete sairia so' na primeira bola.
   */
  marcaDoGesto: number;
  /**
   * Acabou de cair de um salto: o corpo amortece (`Aterrissagem`).
   *
   * Quem decide o que e' "acabou de" e o que e' "salto" e' o `estadoDoMotor`;
   * aqui so' se escolhe o clipe.
   */
  pousando: boolean;
  /** Esperando pra sacar, com a bola na mao. Ver `camadaDoCorpo`. */
  segurandoBola: boolean;
  /** Modulo da velocidade horizontal, em m/s. */
  velocidade: number;
  /**
   * Angulo do ANDAR em relacao a' FRENTE do corpo, em radianos, de -pi a pi.
   *
   * 0 e' andar pra onde se olha; +pi/2 e' andar pra direita de quem olha;
   * -pi/2 pra esquerda; +-pi e' andar de costas.
   */
  anguloDoAndar: number;
}

/** Abaixo disto o corpo esta' parado, e nao "andando bem devagar". */
const PARADO = 0.5;

/**
 * Acima disto e' corrida; entre `PARADO` e isto, caminhada.
 *
 * O atleta so' tem uma velocidade de topo (6,5 m/s), entao a caminhada aparece
 * nas pontas: o primeiro passo depois de parado e a freada antes de parar. Sem
 * ela o boneco sai correndo a toda do repouso, e e' o que mais denuncia
 * animacao presa a estado em vez de a velocidade.
 */
const CORRENDO = 3.2;

/** Meio caminho entre frente e lado: dali em diante o passo e' lateral. */
const QUARTO = Math.PI / 4;
const TRES_QUARTOS = (Math.PI * 3) / 4;

/**
 * A FOLGA das fronteiras, pra quem ja' esta' de um lado delas.
 *
 * Duas teclas juntas dao uma diagonal, e a diagonal com o corpo encarando a
 * rede cai a ~45 graus da frente — EM CIMA da fronteira entre `Run` e
 * `Run_Right`. Sem folga o clipe trocava a cada poucos quadros, e cada troca e'
 * uma mistura de 0,18 s com as pernas em outro ponto do passo: o pe' ia e
 * voltava sem nunca pisar. Com folga, o clipe so' troca quando o andar sai de
 * verdade do setor dele.
 */
const FOLGA_DO_LADO = 0.26;      // ~15 graus
const FOLGA_DA_CORRIDA = 0.5;    // m/s abaixo de CORRENDO pra voltar a andar
const FOLGA_DO_PARADO = 0.15;    // m/s abaixo de PARADO pra parar

/** O angulo do meio de cada setor de corrida. */
const CENTRO_DA_CORRIDA: Readonly<Record<string, number>> = {
  Run: 0, Run_Right: Math.PI / 2, Run_Back: Math.PI, Run_Left: -Math.PI / 2,
};

/** Distancia entre dois angulos, de 0 a pi. */
function entre(a: number, b: number): number {
  const d = Math.abs(a - b) % (Math.PI * 2);
  return d > Math.PI ? Math.PI * 2 - d : d;
}

/**
 * O nome do clipe pra este estado.
 *
 * Os nomes sao os do pack. Se um deles nao existir no modelo carregado, quem
 * toca cai no `Idle` — ver `Animador`.
 *
 * `anterior` e' o clipe que esta' tocando: e' com ele que as fronteiras de
 * andar ganham folga (ver `FOLGA_DO_LADO`). Sem ele, a escolha e' a seca.
 */
export function clipeDoCorpo(estado: EstadoDoCorpo, anterior = ''): string {
  /**
   * Mergulho na frente de tudo, ate' do gesto.
   *
   * Quem mergulha quase sempre esta' salvando a bola, entao o toque chega no
   * meio do voo. Os dois gestos brigariam pelo mesmo braco, e o do mergulho
   * vence porque o `Motor` ja' deitou o corpo pra ele: uma manchete de pe' num
   * corpo deitado sai com os bracos enfiados na areia.
   */
  if (estado.mergulhando || estado.levantando) return 'Mergulho';

  // O gesto vence o pulo: a cortada e' no ar, e a `Cortada` vem com as pernas
  // recolhidas justamente pra poder substituir o `Pulo` la' em cima.
  if (estado.gesto) return CLIPE_DO_TOQUE[estado.gesto];

  if (!estado.noChao) return 'Pulo';

  /**
   * O pouso so' vale abaixo da corrida.
   *
   * O clipe dobra os joelhos com o pe' plantado, e quem cai de um salto ja'
   * correndo pra proxima bola deslizaria agachado pela areia. Correndo, a
   * propria corrida e' o amortecimento.
   */
  if (estado.pousando && estado.velocidade < CORRENDO) return 'Aterrissagem';

  const andando = anterior === 'Walk' || Object.hasOwn(CENTRO_DA_CORRIDA, anterior);
  if (estado.velocidade < PARADO - (andando ? FOLGA_DO_PARADO : 0)) return 'Idle';

  const a = estado.anguloDoAndar;
  const correndo = estado.velocidade >= CORRENDO - (Object.hasOwn(CENTRO_DA_CORRIDA, anterior) ? FOLGA_DA_CORRIDA : 0);

  // De costas e de lado so' existem correndo: o pack nao tem caminhada lateral,
  // e um `Walk` frontal com o corpo indo pro lado e' o defeito que esta funcao
  // existe pra evitar.
  if (!correndo) return 'Walk';

  // Ainda dentro do setor de quem ja' estava tocando, com a folga: fica.
  const centro = CENTRO_DA_CORRIDA[anterior];
  if (centro !== undefined && entre(a, centro) <= QUARTO + FOLGA_DO_LADO) return anterior;

  const volta = Math.abs(a);
  if (volta <= QUARTO) return 'Run';
  if (volta >= TRES_QUARTOS) return 'Run_Back';
  return a > 0 ? 'Run_Right' : 'Run_Left';
}

/**
 * O clipe que toca POR CIMA do corpo, so' nos bracos, ou null.
 *
 * Existe por um caso: o sacador segura a bola e pode andar pela linha de fundo
 * escolhendo de onde sacar. As pernas sao de `clipeDoCorpo` (parado ou
 * andando); o braco que segura e' daqui. Pulando, mergulhando ou batendo, a
 * camada sai — o gesto e o pulo sao do corpo inteiro.
 */
export function camadaDoCorpo(estado: EstadoDoCorpo): string | null {
  if (!estado.segurandoBola || estado.gesto) return null;
  if (!estado.noChao || estado.mergulhando || estado.levantando) return null;
  return 'EsperaDoSaque';
}
